import {
  Button,
  Card,
  DateFormats,
  DescriptionList,
  type DescriptionListItem,
  formatISODate,
  formatNumber,
  Inline,
  Modal,
  type ModalProps,
  Notification,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useEmployee } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import { EmployeeType } from '@omnipresentgroup/types';
import { useMemo } from 'react';

import type { SalaryChangeFormFields } from '../types';

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleCancel: () => void;
  handleSubmit: () => void;
  employeeId: string;
  formValues: SalaryChangeFormFields;
  loading?: boolean;
  showEmployeeTypeNotification?: boolean;
};

const DetailCard = ({
  id,
  label,
  value = '-',
}: {
  id: string;
  label: string;
  value?: string;
}) => {
  return (
    <Card px="32" py="16">
      <Stack gap="8">
        <Typography as="label" size="12" weight="medium" color="subtle" id={id}>
          {label}
        </Typography>
        <Typography aria-labelledby={id} as="span" size="14">
          {value}
        </Typography>
      </Stack>
    </Card>
  );
};

export const AddSalaryChangeReviewModal = ({
  modalProps,
  handleCancel,
  handleSubmit,
  employeeId,
  formValues,
  loading,
  showEmployeeTypeNotification,
}: Props) => {
  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: employeeId,
    enabled: Boolean(employeeId),
  });

  const employeeName = useMemo(() => {
    return `${employee?.personalDetails?.firstName} ${employee?.personalDetails?.lastName}`;
  }, [
    employee?.personalDetails?.firstName,
    employee?.personalDetails?.lastName,
  ]);

  const list: DescriptionListItem[] = useMemo(
    () => [
      {
        header: 'Current gross salary',
        detail: formatNumber({
          input: Number(formValues.currentAnnualGrossSalary?.amount || 0),
          currency: formValues.currentAnnualGrossSalary?.currency,
        }),
      },
      {
        header: 'New gross salary',
        detail: formatNumber({
          input: Number(formValues.newAnnualGrossSalary?.amount || 0),
          currency: formValues.newAnnualGrossSalary?.currency,
        }),
      },
      {
        header: 'Date effective',
        detail: formatISODate(formValues?.effectiveDate, DateFormats.Date),
      },
      {
        header: 'Working hours',
        detail: formValues?.weeklyWorkingHours?.toString() ?? '',
      },
      {
        header: 'Job description',
        detail: formValues?.jobDescription ?? '',
      },
      ...(formValues.enabledNotifications
        ? [
            {
              header: 'Notifications',
              detail: 'Enabled',
            },
          ]
        : []),
    ],
    [
      formValues.currentAnnualGrossSalary?.amount,
      formValues.currentAnnualGrossSalary?.currency,
      formValues.effectiveDate,
      formValues.enabledNotifications,
      formValues?.jobDescription,
      formValues.newAnnualGrossSalary?.amount,
      formValues.newAnnualGrossSalary?.currency,
      formValues?.weeklyWorkingHours,
    ],
  );

  const employeeTypeLabel = useMemo(() => {
    switch (employee?.employeeType) {
      case EmployeeType.VIRTUAL_EMPLOYER: {
        return 'VEO';
      }
      default: {
        return employee?.employeeType;
      }
    }
  }, [employee?.employeeType]);

  return (
    <Modal {...modalProps} span={5}>
      <Modal.Header title="Confirm salary change" description={employeeName} />
      <Modal.Body>
        <Stack>
          {showEmployeeTypeNotification &&
            (employee?.employeeType === EmployeeType.PEO ||
              employee?.employeeType === EmployeeType.VIRTUAL_EMPLOYER) && (
              <Notification
                id="salary-change-employee-type-notification"
                title={`As this employee is hired through our ${employeeTypeLabel} model, you are the legal employer and may need to submit additional paperwork to government authorities to remain compliant.`}
              />
            )}
          <DescriptionList id="add-salary-change-review-fields" list={list} />
          <DetailCard
            id="reason"
            label="Reason"
            value={toSentenceCase(formValues.reason || '')}
          />
          <DetailCard
            id="additionalNotes"
            label="Additional notes"
            value={formValues.additionalNotes}
          />
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoadingEmployee}
            loading={loading}
          >
            Submit
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
